import { EquipmentType } from "../contexts/types";

/** 枝番の正規表現（末尾の -XXX パターン） */
export const REGEX_BRANCH = /-[0-9]{3}$/;

/**
 * 設備情報が変更行（枝番付き）であるか判定する。
 * 設備ID形式：
 * - 通常: Equip-{timestamp}-{random} (3セグメント)
 * - 枝番: Equip-{timestamp}-{random}-{branchNum} (4セグメント以上)
 * @param equipment 設備情報
 * @returns 変更行：true、それ以外：false
 */
export const isModifiedRow = (equipment: EquipmentType): boolean => {
  if (!equipment.equipmentId) return false;
  const segments = equipment.equipmentId.split('-');
  return segments.length > 3 && REGEX_BRANCH.test(equipment.equipmentId);
};

/**
 * 枝番を除いたベースIDを返す
 * @param equipmentId 設備ID
 * @returns ベースID
 */
export const getBaseId = (equipmentId: string): string => {
  return isModifiedRow({ equipmentId } as EquipmentType)
    ? equipmentId.replace(REGEX_BRANCH, "")
    : equipmentId;
};

/**
 * 変更前行（同一ベースIDのisReadonly設備のうち最大枝番）を取得する。
 * ベースID完全一致 + 枝番付きも含めて降順ソートで最大枝番を返す。
 * @param equipmentId 変更行の設備ID
 * @param equipmentList 設備データ一覧
 * @returns 変更前行の設備情報、見つからない場合はnull
 */
export const getMaxReadonlyEquipment = (equipmentId: string, equipmentList: EquipmentType[]): EquipmentType | null => {
  const baseId = getBaseId(equipmentId);
  const readonlyEquipments = equipmentList.filter(eq =>
    eq.isReadonly && eq.equipmentId &&
    (eq.equipmentId === baseId || eq.equipmentId.startsWith(baseId + "-"))
  );
  readonlyEquipments.sort((a, b) => (b.equipmentId || "").localeCompare(a.equipmentId || ""));
  return readonlyEquipments.length > 0 ? readonlyEquipments[0] : null;
};

/**
 * 変更前行のトータル数量を取得する（min値計算用）。
 * 変更行でない場合は0を返す。
 * @param equipment 設備情報
 * @param equipmentList 設備データ一覧
 * @returns 変更前行のトータル数量
 */
export const getBaseTotalQuantity = (equipment: EquipmentType, equipmentList: EquipmentType[]): number => {
  if (!isModifiedRow(equipment) || !equipment.equipmentId) return 0;
  const baseEquipment = getMaxReadonlyEquipment(equipment.equipmentId, equipmentList);
  return baseEquipment?.totalQuantity ?? baseEquipment?.quantity ?? 0;
};

/**
 * トータル数量を計算する。
 * 通常行: quantity そのまま / 変更行: 変更前行のtotalQuantity + quantity
 * @param equipment 設備情報
 * @param equipmentList 設備データ一覧
 * @returns トータル数量
 */
export const calculateTotalQuantity = (equipment: EquipmentType, equipmentList: EquipmentType[]): number => {
  const quantity = equipment.quantity ?? 0;
  if (!isModifiedRow(equipment) || !equipment.equipmentId) {
    return quantity;
  }
  const baseEquipment = getMaxReadonlyEquipment(equipment.equipmentId, equipmentList);
  const baseTotalQuantity = baseEquipment?.totalQuantity ?? baseEquipment?.quantity ?? 0;
  return baseTotalQuantity + quantity;
};

/**
 * 枝番追加ボタンの非活性判定。
 * すでに同一ベースIDで編集可能な設備が存在する場合は非活性（true）。
 * @param equipmentId 設備ID
 * @param equipmentList 設備データ一覧
 * @returns 非活性：true、活性：false
 */
export const isDisabledAddBranch = (equipmentId: string, equipmentList: EquipmentType[]): boolean => {
  const baseId = getBaseId(equipmentId);
  return equipmentList.some(e => e.equipmentId?.startsWith(baseId) && !e.isReadonly);
};

/**
 * displayOrder再採番（processIdごと、固定データの最大値から連番）。
 * - 固定データ（isReadonly or 枝番）: displayOrderを維持
 * - 新規データ: processIdごとに固定データの最大整数値+1から連番
 * @param data 設備データ配列（破壊的に変更）
 * @param isEmptyFn 空行判定関数
 */
export const renumberDisplayOrder = (
  data: EquipmentType[],
  isEmptyFn: (e: EquipmentType) => boolean,
): void => {
  // processIdでグループ化
  const groups = new Map<string, EquipmentType[]>();
  data.forEach(item => {
    const pid = item.processId ?? "";
    if (!groups.has(pid)) {
      groups.set(pid, []);
    }
    groups.get(pid)!.push(item);
  });

  // processIdごとに再採番
  groups.forEach(items => {
    // 固定データ（isReadonly or 枝番）の最大displayOrder整数部を取得
    const fixedMaxOrder = items
      .filter(e => (e.isReadonly || isModifiedRow(e)) && e.displayOrder != null)
      .reduce((max, e) => Math.max(max, Math.floor(e.displayOrder || 0)), 0);

    let nextOrder = fixedMaxOrder;

    items.forEach(item => {
      if (item.isReadonly || isModifiedRow(item)) {
        // 固定データ: displayOrderを維持
      } else if (!isEmptyFn(item)) {
        // 新規データ: 連番を振る
        nextOrder += 1;
        item.displayOrder = nextOrder;
      }
    });
  });
};
